/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable max-len */
/* eslint-disable no-nested-ternary */
import React, {
  useState, useRef, useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import IcomoonReact from 'icomoon-react';
import { debounce } from 'lodash';
import ClipLoader from 'react-spinners/ClipLoader';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import useValidateUrl from './hooks/useValidateUrl';

import { saveCalendarExercise, saveNonDateBasedWorkoutExercise } from '../../ducks/calendarActions';

const Container = styled('div')`
  display: flex;
  width: 100%;
  justify-content: space-between;
  gap: 16px;
`;

const VideoUrlInput = styled('input')`
  display: flex;
  width: 100%; 
  border: 1px solid #EEEEEE;
  border-radius: 4px;
  min-height: 34px;
  max-height: 34px;
  min-width: 200px;
  background: #FCFCFC;
  padding-left: 15px;
  padding-right: 30px;
  color: #424242;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 400;


   &::placeholder {
    font-family: 'Nunito Sans', sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: #9E9E9E;
  }
`;

const InputContainer = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const InputTitle = styled('div')`
  font-size: 16px;
  color: #424242;
  font-family: 'Nunito Sans';
  text-decoration: ${(props) => props.clickableLink && 'underline'};
  color: ${(props) => props.clickableLink && 'blue'};
  cursor: ${(props) => props.clickableLink && 'pointer'};
  width: fit-content;
`;

const Wrapper = styled('div')`
  display: flex;
  width: 100%;
  position: relative;
`;

const LoaderWrapper = styled('div')`
  position: absolute;
  top: 7px;
  right: 5px;
`;

const PreSaveVideoTable = ({
  contentIndex,
  content,
  date,
  dayNumber,
}) => {
  const dispatch = useDispatch();
  const {
    handleValidateUrl,
  } = useValidateUrl();

  // eslint-disable-next-line no-unused-vars
  const [isAdding, setIsAdding] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [videoTitle, setVideoTitle] = useState('');
  const [passedUrlValidation, setPassedUrlValidation] = useState(false);
  const [urlValidationError, setUrlValidationError] = useState(null);
  const [urlValidationLoading, setUrlValidationLoading] = useState(false);

  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const effectiveDate = date || selectedDate;
  const programType = useSelector((state) => state.calendar.data.programType);

  const errorAddReset = () => {
    setIsAdding(false);
  };

  const extractVideoId = (videoString) => {
    const match = videoString.match(/^Video-(\d+)$/);
    return match ? parseInt(match[1], 10) : null;
  };

  const debouncedHandleValidateVideo = useRef(
    debounce((
      dbUrl,
    ) => {
      if (dbUrl) {
        setUrlValidationLoading(true);
        handleValidateUrl(dbUrl, setUrlValidationLoading, setPassedUrlValidation, setUrlValidationError);
      }
    }, 500),
  ).current;

  useEffect(() => {
    if (videoUrl) {
      setPassedUrlValidation(false);
      setUrlValidationError(null);
      debouncedHandleValidateVideo(videoUrl, videoTitle);
    }
  }, [videoUrl]);

  /** we only want to hit the save endpoint if the url passes iframely validation
   for a valid url entry
   * we also want to make sure that if a user enters a url that passes and then modifies
   that url during the 2000 timeout that we stop the previous call from going through
   we always want to make sure the save function is being hit with the most recent url update
   that is why we are using a saveTimeoutRef
   */
  const saveTimeoutRef = useRef(null);

  useEffect(() => {
    /** if there is a save function that currently exists we want to clear it/
     * stop if from executing if the user is modifying the url
     * so basically we are creating a new saveCalendarExercise funtion every time
     * one of the dependencies change and canceling one if it's in progress during a url change
     */
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    if (videoUrl && !urlValidationLoading && passedUrlValidation) {
      saveTimeoutRef.current = setTimeout(() => {
        const submissionItem = {
          assignedId: extractVideoId(content.assignedId),
          /** Conditional key based on programType: "date" OR "dayNumber" */
          [programType === 'free-form-program' ? 'dayNumber' : 'date']:
              programType === 'free-form-program'
                ? dayNumber
                : moment(effectiveDate).format('YYYY-MM-DD'),
          order: contentIndex,
          description: content?.exercise?.description || '',
          type: 'video',
          url: videoUrl,
          popUp: false,
        };
        if (videoTitle) {
          submissionItem.title = videoTitle;
        }
        if (content.groupingLetter) {
          submissionItem.groupingLetter = content.groupingLetter;
        }
        if (programType === 'free-form-program') {
          dispatch(saveNonDateBasedWorkoutExercise(
            currentUser,
            contentIndex,
            submissionItem,
            dayNumber,
            errorAddReset,
            { exerciseName: content.title },
            content.assignedId,
          ));
        } else {
          dispatch(saveCalendarExercise(
            currentUser,
            contentIndex,
            submissionItem,
            moment(effectiveDate).format('YYYY-MM-DD'),
            errorAddReset,
            { exerciseName: content.title },
            content.assignedId,
          ));
        }
      }, 2000);
    }
  }, [videoUrl, videoTitle, passedUrlValidation, urlValidationLoading]);

  useEffect(() => () => clearTimeout(saveTimeoutRef.current), []);

  return (
    <Container>
      <InputContainer>
        {/* <InputTitle>
          Video Url
        </InputTitle> */}
        <InputTitle
          clickableLink={videoUrl && passedUrlValidation && !urlValidationLoading}
          onClick={() => {
            if (videoUrl && passedUrlValidation && !urlValidationLoading) {
              window.open(videoUrl, '_blank');
            }
          }}
        >
          Video Url
        </InputTitle>
        <Wrapper>
          <VideoUrlInput
            value={videoUrl}
            placeholder='Paste video URL'
            onChange={(e) => {
              setVideoUrl(e.target.value);
            }}
          />
          {videoUrl && urlValidationError && !urlValidationLoading && (
          <IcomoonReact
            color='#CC0404'
            icon='remove'
            iconSet={iconSet}
            size={20}
            style={{
              border: '1px solid #CC0404',
              borderRadius: '50%',
              top: 7,
              right: 5,
              padding: '1px',
              position: 'absolute',
            }}
          />
          )}
          {videoUrl && passedUrlValidation && !urlValidationLoading && (
          <IcomoonReact
            color=' #0DCC8A'
            icon='completion-circle'
            iconSet={iconSet}
            size={20}
            style={{
              borderRadius: '50%',
              top: 7,
              right: 5,
              padding: '1px',
              position: 'absolute',
            }}
          />
          )}
          <LoaderWrapper>
            <ClipLoader
              color='grey'
              loading={urlValidationLoading}
              size={20}
              aria-label='Loading Spinner'
              data-testid='loader'
            />
          </LoaderWrapper>
        </Wrapper>
      </InputContainer>
      <InputContainer>
        <InputTitle>
          Video Title
        </InputTitle>
        <VideoUrlInput
          value={videoTitle}
          placeholder='Add title (optional)'
          onChange={(e) => {
            setVideoTitle(e.target.value);
          }}
        />
      </InputContainer>
    </Container>
  );
};

PreSaveVideoTable.propTypes = {
  contentIndex: PropTypes.number.isRequired,
  content: PropTypes.instanceOf(Object).isRequired,
  date: PropTypes.string,
  dayNumber: PropTypes.string,
};

PreSaveVideoTable.defaultProps = {
  date: null,
  dayNumber: null,
};

export default PreSaveVideoTable;
